import { ImageType, s3, warmS3Credentials } from "@/common/globals"
import { AttractionType, ImageOrientationEnum } from "@/common/types"
import ControlledCheckboxGroup from "@/components/controlledInputs/ControlledCheckBoxGroup"
import ControlledGoogleAddress from "@/components/controlledInputs/ControlledGoogleAddress"
import ControlledTextInput from "@/components/controlledInputs/ControlledTextInput"
import FileUploader from "@/components/FileUploader"
import useCreateAttraction from "@/hooks/api/attractions/useCreateAttraction"
import useListAttractionTypes from "@/hooks/api/attractions/useListAttractionTypes"
import { PutObjectCommand } from "@aws-sdk/client-s3"
import { zodResolver } from "@hookform/resolvers/zod"
import { Alert, Box, Button, Stack, Typography } from "@mui/material"
import { useEffect, useState } from "react"
import { FileWithPath } from "react-dropzone"
import { fromAddress, OutputFormat, setDefaults } from "react-geocode"
import { useForm, SubmitHandler, FieldError } from "react-hook-form"
import { z } from "zod"

const attractionFormSchema = z.object({
  title: z
    .string()
    .min(1, "Title is required.")
    .max(255, "Title is too long, max 255 char."),
  description: z
    .string()
    .min(1, "Description is required.")
    .max(500, "Description is too long, max 500 char."),
  startDate: z.date(),
  location: z.string(),
  address: z.string(),
  position: z.object({
    lat: z.number(),
    long: z.number(),
  }),
  price: z.string(),
  attraction_types: z.array(z.number()),
  actionUrl: z.string(),
  images: z.array(
    z.object({
      file: z.custom<FileWithPath>(),
      orientation: z.nativeEnum(ImageOrientationEnum),
      order: z.number(),
    })
  ),
})

type SubmitResult = {
  success: boolean
  message: string
}

export type AttractionFormDataType = z.infer<typeof attractionFormSchema>

const defaultValues: AttractionFormDataType = {
  title: "",
  description: "",
  startDate: new Date(),
  location: "",
  address: "",
  position: { lat: 0, long: 0 },
  price: "0.00",
  attraction_types: [],
  actionUrl: "",
  images: [],
}

export default function CreateAttraction() {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  )
  const [submitMessageResult, setSubmitMessageResult] = useState(
    null as SubmitResult | null
  )

  const [horizontalFiles, setHorizontalFiles] = useState<ImageType[]>([])
  const [verticalFiles, setVerticalFiles] = useState<ImageType[]>([])
  const { data: attractionTypes, isLoading } = useListAttractionTypes()
  const { mutateAsync: createAttraction } = useCreateAttraction()

  const {
    handleSubmit,
    // reset,
    control,
    setValue,
    formState: { errors },
  } = useForm<AttractionFormDataType>({
    resolver: zodResolver(attractionFormSchema),
    defaultValues: defaultValues,
  })

  const submitAttraction: SubmitHandler<AttractionFormDataType> = async (
    data
  ) => {
    const allImages = horizontalFiles.concat(verticalFiles)

    // Convert File objects to plain objects for JSON serialization
    // (File properties aren't enumerable and won't survive JSON.stringify)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data.images = allImages.map((image) => ({
      file: {
        name: image.file.name,
        path: image.file.path,
        lastModified: image.file.lastModified,
        size: image.file.size,
        type: image.file.type,
      },
      orientation: image.orientation,
      order: image.order,
    })) as any

    // convert the lat, long
    setDefaults({
      key: import.meta.env.VITE_GOOGLE_MAP_KEY, // Your API key here.
      language: "en", // Default language for responses.
      region: "us", // Default region for responses.
      outputFormat: OutputFormat.JSON,
    })

    await fromAddress(data.address)
      .then(({ results }) => {
        const { lat, lng } = results[0].geometry.location
        data.position = { lat: lat, long: lng }
      })
      .catch(console.error)

    // Ensure S3 credentials are ready before uploading
    await warmS3Credentials()

    createAttraction(data).then(async (res) => {
      const attraction = res.data

      for (const file of allImages) {
        const params = {
          Bucket: `${import.meta.env.VITE_AWS_ATTRACTIONS_BUCKET}`,
          Key: `${attraction.insertId}/${file.file.name}`,
          Body: file.file,
          ContentType: file.file.type,
        }

        try {
          await s3.send(new PutObjectCommand(params))
        } catch (err) {
          console.error("There was a problem uploading images", err)
        }
      }

      setSubmitMessageResult({
        success: true,
        message: `Successfully created new attraction #${attraction.insertId}`,
      })
    })
  }

  const handleSetValue = (value: string) => {
    setValue("address", value)
  }

  // Utility function to get the first error message with the field name
  const getFirstErrorMessage = (
    errors: Record<string, FieldError | undefined>
  ): string | undefined => {
    for (const [key, value] of Object.entries(errors)) {
      if (value) {
        console.error(key)
        return `${value.message}`
      }
    }
    return undefined
  }

  useEffect(() => {
    // handle errors
    const firstErrorMessage = getFirstErrorMessage(Object(errors))
    setErrorMessage(firstErrorMessage)
  }, [errors])

  if (isLoading) {
    return <>Loading...</>
  }

  return (
    <Box>
      {submitMessageResult && (
        <Alert
          severity={submitMessageResult.success ? "success" : "error"}
          sx={{ marginBottom: "1rem" }}
        >
          {submitMessageResult.message}
        </Alert>
      )}
      <form onSubmit={handleSubmit(submitAttraction)}>
        <Stack spacing={1}>
          <ControlledTextInput
            control={control}
            name="title"
            label="Title"
            type="text"
          />
          <ControlledTextInput
            control={control}
            name="description"
            label="Description"
            type="text"
          />

          <ControlledTextInput
            control={control}
            name="location"
            label="Location Title"
            type="text"
          />
          <ControlledGoogleAddress
            control={control}
            name="address"
            label="Address"
            setValue={handleSetValue}
          />
          <ControlledTextInput
            control={control}
            name="price"
            label="Price"
            type="text"
          />
          <ControlledCheckboxGroup
            control={control}
            name="attraction_types"
            options={attractionTypes.data.map(
              (attractionType: AttractionType) => {
                return {
                  label: attractionType.human_name,
                  value: attractionType.attraction_type_id,
                }
              }
            )}
          />
          <ControlledTextInput
            control={control}
            name="actionUrl"
            label="Action Url"
          />
          <FileUploader
            horizontalFiles={horizontalFiles}
            setHorizontalFiles={setHorizontalFiles}
            verticalFiles={verticalFiles}
            setVerticalFiles={setVerticalFiles}
          />
        </Stack>
        <Box sx={{ textAlign: "center", padding: "1rem" }}>
          {errorMessage && (
            <Typography variant="caption" color="text.warning">
              {errorMessage}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          // disabled={notReadyToSubmit}
          type="submit"
        >
          Create Attraction
        </Button>
      </form>
    </Box>
  )
}
